<template>
  <v-row>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="templateLocal.name"
        label="Bezeichnung"
        required
        outlined
        hide-details="auto"
      ></v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-autocomplete
        v-model="templateLocal.category"
        :items="categoryOptions"
        item-text="title"
        return-object
        label="Kategorie"
        outlined
        hide-details="auto"
        small-chips
      >
        <template v-slot:item="{ item, on, attrs }">
          <v-subheader v-if="item.header" class="px-0">
            {{ item.header }}
          </v-subheader>
          <v-list-item v-else v-on="on" v-bind="attrs">
            <v-list-item-icon class="mr-3">
              <v-icon small>mdi-{{ item.icon || "shape" }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" md="6">
      <v-select
        v-model="templateLocal.placeholderContext"
        :items="contextOptions"
        label="Verfügbare Datenquellen"
        outlined
        hide-details="auto"
        prepend-inner-icon="mdi-database"
        hint="Bestimmt, welche Datenquellen in der Vorlage verfügbar sind"
        persistent-hint
      ></v-select>
    </v-col>
    <v-col cols="12" md="6">
      <v-select
        v-model="templateLocal.usageContexts"
        :items="usageContextOptions"
        label="Verwendungskontext festlegen"
        outlined
        hide-details="auto"
        hint="Bestimmt, an welchen Stellen in der Anwendung die Dokumentvorlage verwendet werden kann"
        persistent-hint
        multiple
        chips
        small-chips
        deletable-chips
      ></v-select>
    </v-col>
    <v-col cols="12">
      <v-radio-group
        v-model="templateLocal.dataMode"
        label="Datenmodus"
        mandatory
        hide-details="auto"
      >
        <v-radio value="single">
          <template v-slot:label>
            <div>
              <span>Einzeldatensatz</span>
              <div class="text-caption text--secondary">
                Die Vorlage wird mit den Daten eines einzelnen Datensatzes
                befüllt (z.B. Vertrag, Bescheinigung, Urkunde)
              </div>
            </div>
          </template>
        </v-radio>
        <v-radio value="multi">
          <template v-slot:label>
            <div>
              <span>Mehrere Datensätze</span>
              <div class="text-caption text--secondary">
                Die Vorlage wird mit einer Liste von Datensätzen befüllt (z.B.
                Etiketten, Adresslisten, Telefonlisten)
              </div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </v-col>
    <v-col cols="12">
      <v-radio-group
        v-model="templateLocal.usagePolicy"
        label="Nutzungsrichtlinie für Verwendung der Dokumentvorlage"
        mandatory
        hide-details="auto"
      >
        <v-radio
          label="Dokumentvorlage darf von allen Benutzern verwendet werden"
          value="noRestriction"
        ></v-radio>
        <v-radio
          label="Dokumentvorlage darf von allen Benutzern verwendet werden mit ausgewählten Labeln"
          value="labelRestriction"
        ></v-radio>
        <v-radio
          label="Dokumentvorlage darf nur innerhalb von Prozessaufgaben verwendet werden"
          value="processRestriction"
          disabled
        ></v-radio>
      </v-radio-group>
    </v-col>
    <v-col cols="12">
      <v-textarea
        v-model.trim="templateLocal.description"
        label="Beschreibung (optional)"
        outlined
        hide-details="auto"
        prepend-inner-icon="mdi-text"
        rows="2"
      ></v-textarea>
    </v-col>
  </v-row>
</template>

<script>
import { MEMBER_MANAGEMENT, AVAILABILITY } from "@/store/modules.js";
import {
  GET_ASSIGNMENTS,
  GET_UNITS,
  GET_DIVISIONS,
} from "@/store/action-types.js";
import {
  USAGE_CONTEXTS,
  PLACEHOLDER_CONTEXTS,
} from "@/util/documentTemplateService.js";

export default {
  name: "document-template-editor",
  props: {
    template: {
      type: Object,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },
  model: {
    prop: "template",
    event: "templateChange",
  },
  data() {
    return {};
  },
  computed: {
    templateLocal: {
      get: function () {
        return this.template;
      },
      set: function (value) {
        this.$emit("templateChange", value);
      },
    },
    contextOptions() {
      // Build from PLACEHOLDER_CONTEXTS, excluding global-only contexts (organization)
      return Object.entries(PLACEHOLDER_CONTEXTS)
        .filter(([, config]) => !config.isGlobal)
        .map(([key, config]) => ({
          text: config.label,
          value: key,
        }));
    },
    usageContextOptions() {
      // Show usage contexts whose placeholderContexts array includes the selected primary context
      return Object.entries(USAGE_CONTEXTS)
        .filter(([, config]) =>
          config.placeholderContexts.includes(
            this.templateLocal.placeholderContext,
          ),
        )
        .map(([key, config]) => ({
          text: config.label,
          value: key,
        }));
    },
    categoryOptions() {
      const options = [];

      // Assignments
      const assignments = this.$store.state.memberManagement.assignments || [];
      const sortedAssignments = assignments
        .map((a) => ({
          id: a.meta.id,
          title: a.title,
          shortTitle: a.shortTitle || a.title,
          sortKey: a.sortKey || 0,
          icon: a.icon || "briefcase",
          type: "assignment",
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      if (sortedAssignments.length > 0) {
        options.push({ header: "Aufgabengebiete", type: "header" });
        options.push(...sortedAssignments);
      }

      // Units
      const units = this.$store.state.memberManagement.units || [];
      const sortedUnits = units
        .map((u) => ({
          id: u.meta.id,
          title: u.title,
          shortTitle: u.shortTitle || u.title,
          sortKey: u.sortKey || 0,
          icon: "fire-truck",
          type: "unit",
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      if (sortedUnits.length > 0) {
        options.push({ header: "Einheiten", type: "header" });
        options.push(...sortedUnits);
      }

      // Divisions
      const divisions = this.$store.state.memberManagement.divisions || [];
      const sortedDivisions = divisions
        .map((d) => ({
          id: d.meta.id,
          title: d.title,
          shortTitle: d.shortTitle || d.title,
          sortKey: d.sortKey || 0,
          icon: "home-group",
          type: "division",
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      if (sortedDivisions.length > 0) {
        options.push({ header: "Abteilungen", type: "header" });
        options.push(...sortedDivisions);
      }

      // Other
      options.push({ header: "Sonstige", type: "header" });
      options.push({
        id: "other",
        title: "Sonstige",
        shortTitle: "Sonstige",
        sortKey: 9999,
        icon: "dots-horizontal",
        type: "other",
      });

      return options;
    },
    isValid() {
      return (
        !!this.templateLocal.name &&
        !!this.templateLocal.category &&
        !!this.templateLocal.placeholderContext &&
        !!this.templateLocal.dataMode
      );
    },
  },
  watch: {
    isValid: {
      immediate: true,
      handler(val) {
        this.$emit("valid", val);
      },
    },
  },
  mounted() {
    this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_ASSIGNMENTS}`, {
      organizationId: this.organizationId,
    });
    this.$store.dispatch(`${AVAILABILITY}${GET_UNITS}`, {
      organizationId: this.organizationId,
    });
    this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_DIVISIONS}`, {
      organizationId: this.organizationId,
    });
  },
};
</script>
